import React, { useCallback } from 'react';
import { useFocusEffect } from '@react-navigation/native';

import { useIsLoggedIn } from '@order/AuthMachine';
import { Bag } from '@order/features/ordering';
import { useCloseBag } from '@order/hooks';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { StripePaymentProvider } from '@order/stripe';
import { getEnvVars } from '@order/utils';

import { BagAnimatedDialog, BagContent } from './components';
import {
  useBagGestures,
  useOrderState,
  useOrderSubmissionResponseHandler,
} from './hooks';

export const BagScreen = () => {
  const isExpressPaymentsEnabled = useFeatureFlag(
    'permanent-order-stripe-express-checkout-enabled',
  );

  const isStripePaymentsSdkEnabled = useFeatureFlag(
    'permanent-order-stripe-payment-sdk-enabled',
    { listenForChanges: true }, // might turn to true after logging in while bag is still mounted.
  );

  const { orderSubmissionRef, handleOrderSubmissionResponse } =
    useOrderSubmissionResponseHandler();

  const orderState = useOrderState({
    isStripePaymentsSdkEnabled,
    isExpressPaymentsEnabled,
    handleOrderSubmissionResponse,
  });

  const {
    isFetchingInitialData,
    total,
    stripeSession,
    isSubmittingOrder,
    isShowingModal,
    startOrdering,
  } = orderState;

  const isLoggedIn = useIsLoggedIn();
  const shouldRenderAnimatedDialog =
    isLoggedIn && (!isStripePaymentsSdkEnabled || !isFetchingInitialData);

  // ──────────────────────────────────────────────────────────────────────────

  const closeBag = useCloseBag();

  // Prevent closing bag while submitting an order.
  const handleCloseBag = useCallback(() => {
    if (isSubmittingOrder) return;

    closeBag();
  }, [isSubmittingOrder,closeBag]);

  // Enable/disable gestures when modal is shown.
  useBagGestures(isShowingModal);

  // Fetch ordering data on bag focus.
  useFocusEffect(startOrdering);

  // ──────────────────────────────────────────────────────────────────────────

  return (
    <Bag.DynamicModal onClose={handleCloseBag}>
      <BagContent orderState={orderState} />

      {shouldRenderAnimatedDialog ? (
        <StripePaymentProvider
          publicKey={publicKey}
          amount={total}
          sessionId={stripeSession}
        >
          <BagAnimatedDialog
            orderState={orderState}
            orderSubmissionRef={orderSubmissionRef}
          />
        </StripePaymentProvider>
      ) : null}
    </Bag.DynamicModal>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const publicKey = getEnvVars().STRIPE_PUBLIC_KEY as string;
